import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { isValidNumericAmount } from "@/lib/price-utils";
import { DiscountInput } from "./DiscountInput";

interface PricingOptionsProps {
  markupPercent: number;
  onMarkupPercentChange: (value: number) => void;
  discountPercentage: number;
  onDiscountPercentageChange: (value: number) => void;
  discountDescription?: string;
  onDiscountDescriptionChange?: (value: string) => void;
  totalAmount: number;
  className?: string;
}

/**
 * A component for inputting pricing options (markup, parts affect price, discount)
 */
export function PricingOptions({
  markupPercent,
  onMarkupPercentChange,
  discountPercentage,
  onDiscountPercentageChange,
  discountDescription = "",
  onDiscountDescriptionChange,
  totalAmount,
  className = "bg-muted/30 p-4 rounded-md",
}: PricingOptionsProps) {
  // Use string state for controlled input to properly handle editing
  const { t } = useTranslation();
  const [markupPercentStr, setMarkupPercentStr] = useState<string>("");

  // Initialize form values when props change (e.g., template selection)
  const markupPercentInitialized = useRef(false);
  const prevMarkupPercent = useRef(markupPercent);
  const userIsTyping = useRef(false);

  useEffect(() => {
    // Don't update if user is actively typing
    if (userIsTyping.current) {
      userIsTyping.current = false;
      return;
    }

    // Reset initialization flag if prop actually changed (template switch)
    if (prevMarkupPercent.current !== markupPercent) {
      markupPercentInitialized.current = false;
      prevMarkupPercent.current = markupPercent;
    }

    if (!markupPercentInitialized.current) {
      setMarkupPercentStr(markupPercent > 0 ? markupPercent.toString() : "");
      markupPercentInitialized.current = true;
    }
  }, [markupPercent]);

  // Input handler with max value validation
  const handleMarkupPercentChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;

    // Allow valid numeric values with up to 1 decimal place for percentage (e.g., 15.5)
    if (isValidNumericAmount(value, 1)) {
      const numericValue = value === "" ? 0 : parseFloat(value);

      // Check maximum value (999.9) - reasonable max for markup percentage
      if (numericValue <= 999.9) {
        userIsTyping.current = true;
        setMarkupPercentStr(value);
        onMarkupPercentChange(numericValue);
      }
    }
  };

  return (
    <div className={className}>
      <div className="space-y-4">
        {/* Markup Percentage Input */}
        <div className="grid grid-cols-[120px_1fr] gap-3 items-center">
          <Label htmlFor="markup-percent" className="text-sm">
            {t("components.quotes.pricingOptions.partsMarkup")}
          </Label>
          <div className="relative">
            <Input
              id="markup-percent"
              type="text"
              value={markupPercentStr}
              onChange={handleMarkupPercentChange}
              placeholder="0.0"
              max="999.9"
              className="pr-6"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <span className="text-gray-500 text-sm">%</span>
            </div>
          </div>
        </div>

        {/* Discount Input - Label Above, Controls Right-Aligned */}
        <div className="space-y-2">
          <Label htmlFor="discount" className="text-sm font-medium">
            {t("components.quotes.pricingOptions.discount")}
          </Label>
          <div className="flex justify-end">
            <div className="w-80 max-w-[80%]">
              <DiscountInput
                discountPercentage={discountPercentage}
                onDiscountPercentageChange={onDiscountPercentageChange}
                totalAmount={totalAmount}
              />
            </div>
          </div>
        </div>

        {/* Discount Description */}
        {onDiscountDescriptionChange && discountPercentage > 0 && (
          <div className="space-y-2">
            <Label
              htmlFor="discount-description"
              className="text-sm font-medium"
            >
              {t("components.quotes.pricingOptions.discountNotes")}
            </Label>
            <div className="flex justify-end">
              <div className="w-80 max-w-[80%]">
                <Textarea
                  id="discount-description"
                  value={discountDescription}
                  onChange={(e) => onDiscountDescriptionChange(e.target.value)}
                  placeholder={t("components.quotes.pricingOptions.optionalDescriptionForThis")}
                  className="min-h-[80px] resize-none text-sm"
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
